import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getExchangeRateList,
  getExchangeRateById,
  deleteExchangeRateById,
  updateExchangeRateById,
  createExchangeRate,
  uploadExchangeRateQuery,
} from "./queries";

const ExchangeRateContext = createContext({});

export const useExchangeRateContext = () => useContext(ExchangeRateContext);

export const ExchangeRateProvider = ({ ...props }) => {
  const [exchangeRatePerId, setExchangeRatePerId] = useState({});
  const [exchangeRateList, setExchangeRateList] = useState({
    data: [],
    limit: 25,
    total: 0,
    page: 1,
  });

  const exchangeRateListGetter = useCallback(async (page = 1, limit = 25) => {
    try {
      setExchangeRateList(await getExchangeRateList(page, limit));
    } catch (e) {
      console.error("exchangeRateListGetter", e);
      throw e;
    }
  }, []);

  const exchangeRateByIdGetter = useCallback(async (id) => {
    try {
      setExchangeRatePerId(await getExchangeRateById(id));
    } catch (e) {
      console.error("exchangeRateByIdGetter", e);
      throw e;
    }
  }, []);

  const exchangeRateDeleteById = useCallback(async (id) => {
    try {
      await deleteExchangeRateById(id);
      exchangeRateListGetter();
    } catch (e) {
      console.error("exchangeRateDeleteById", e);
      throw e;
    }
  }, []);

  const exchangeRateUpdateById = useCallback(async (id, data) => {
    try {
      data.base_currency_id = parseInt(data.base_currency_id, 10);
      data.objective_currency_id = parseInt(data.objective_currency_id, 10);
      data.country_id = parseInt(data.country_id, 10);
      const updatedExchangeRate = await updateExchangeRateById(id, data);
      setExchangeRatePerId(updatedExchangeRate);
      exchangeRateListGetter();
    } catch (e) {
      console.error("exchangeRateUpdateById", e);
      throw e;
    }
  }, []);

  const exchangeRateCreate = useCallback(async (data) => {
    try {
      const { base_currency_id, objective_currency_id, country_id } = data;
      await createExchangeRate({
        ...data,
        base_currency_id: Number(base_currency_id),
        objective_currency_id: Number(objective_currency_id),
        country_id: Number(country_id),
      });
      exchangeRateListGetter();
    } catch (e) {
      console.error("exchangeRateCreate", e);
      throw e;
    }
  }, []);

  const uploadExchangeRate = useCallback(async (id, file) => {
    try {
      await uploadExchangeRateQuery(id, file);
      exchangeRateListGetter();
    } catch (e) {
      console.error("uploadExchangeRate", e);
      throw e;
    }
  }, []);

  const values = useMemo(
    () => ({
      exchangeRatePerId,
      setExchangeRatePerId,
      exchangeRateListGetter,
      exchangeRateByIdGetter,
      exchangeRateDeleteById,
      exchangeRateList,
      exchangeRateUpdateById,
      exchangeRateCreate,
      uploadExchangeRate,
    }),
    [
      exchangeRatePerId,
      setExchangeRatePerId,
      exchangeRateListGetter,
      exchangeRateByIdGetter,
      exchangeRateDeleteById,
      exchangeRateList,
      exchangeRateUpdateById,
      exchangeRateCreate,
      uploadExchangeRate,
    ]
  );

  return <ExchangeRateContext.Provider value={values} {...props} />;
};
